import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import formatDate from "../../../components/utils/DateFormatter";

const getStatusStyle = (status) => {
    switch (status) {
        case "approved":
            return { background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600" };
        case "partially_approved":
            return { background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600" };
        case "rejected":
            return { background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600" };
        default:
            return { background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600" };
    }
};

const BranchInventoryRequestHistory = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const { permissions } = useContext(AuthContext);
    const navigate = useNavigate();

    const [filters, setFilters] = useState({
        per_page: 15,
        page: 1,
        start_date: "",
        end_date: "",
        branch_id: "",
        head_office_id: "",
    });


    const [filterLoading, setFilterLoading] = useState(false);
    const [clearFilterLoading, setClearFilterLoading] = useState(false);

    const fetchHistory = async (payload = filters) => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) throw new Error("No access token found.");

            const response = await axios.get(ApiServices.getBranchInventoryRequestHistory, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                params: payload,
                withCredentials: true,
            });

            const { status, data } = response.data;
            if (status === 200 && data) {
                setRequests(data.requests || []);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    const isAnyFilterFilled = () => Object.values(filters).some((v) => v !== "" && v !== null && v !== 15 && v !== 1);

    const handleFilterClick = async () => {
        if (!isAnyFilterFilled()) return;
        setFilterLoading(true);
        await fetchHistory(filters);
        setFilterLoading(false);
    };

    const handleClearFilters = async () => {
        const defaultFilters = { per_page: 15, page: 1, start_date: "", end_date: "", branch_id: "", head_office_id: "" };
        setFilters(defaultFilters);
        setClearFilterLoading(true);
        await fetchHistory(defaultFilters);
        setClearFilterLoading(false);
    };

    const handleFilter = (e) => {
        setFilterText(e.target.value);
    };


    const ExpandableComponent = ({ data }) => (
        <div className="flex justify-start p-4 relative">
            {permissions.some(p => p.name === 'admin.view') && (
                <div className="mx-2 my-2 group relative">
                    <Link
                        to="/operations/branch/inventory-requests/details"
                        state={{ requestId: data.id, fromHistory: true }}
                    >
                        <Eye className="w-5 h-5 text-dash4" />
                    </Link>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-darkGreen3 text-xs px-2 py-1 rounded">
                        Details
                    </div>
                </div>
            )}
        </div>
    );

    const columns = [
        {
            name: "Request No",
            selector: (row) => row.request_number || "N/A",
            sortable: true,
            width: "200px",
        },
        {
            name: "Branch",
            selector: (row) => row.branch_name?.toUpperCase() || "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Head Office",
            selector: (row) => row.head_office_name || "N/A",
            sortable: true,
            width: "200px",
        },
        {
            name: "Status",
            selector: (row) => row.status || "N/A",
            sortable: true,
            width: "160px",
            cell: (row) => (
                <span style={getStatusStyle(row.status)}>
                    {row.status?.replace(/_/g, " ").toUpperCase()}
                </span>
            ),
        },
        {
            name: "Qty Requested",
            selector: (row) => row.total_quantity_requested || 0,
            sortable: true,
            width: "140px",
        },
        {
            name: "Qty Approved",
            selector: (row) => row.total_quantity_approved || 0,
            sortable: true,
            width: "140px",
            cell: (row) => (
                <span style={Number(row.total_quantity_approved) === 0 ? { color: "#dc2626", fontWeight: "600" } : {}}>
                    {row.total_quantity_approved || 0}
                </span>
            ),
        },
        {
            name: "Total Value",
            selector: (row) => row.total_value || 0,
            sortable: true,
            width: "150px",
            cell: (row) => CurrencyFormatter(row.total_value || 0),
        },
        {
            name: "Requested At",
            selector: (row) => formatDate(row.requested_at) || "N/A",
            width: "180px",
        },
        {
            name: "Reviewed At",
            selector: (row) => formatDate(row.reviewed_at) || "N/A",
            width: "180px",
        },
    ];

    const filteredItems = requests.filter((item) =>
        item.request_number?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.branch_name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.head_office_name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.status?.toLowerCase().includes(filterText.toLowerCase())
    );

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Operations</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Branch</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Inventory Request History</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                {/* Filters */}
                <div className="pb-4 bg-white dark:bg-gray-900 mt-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            type="search"
                            className="block pl-4 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 min-w-[200px]"
                            placeholder="Search..."
                            value={filterText}
                            onChange={handleFilter}
                        />
                        <input
                            type="date"
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={filters.start_date}
                            onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))}
                        />
                        <input
                            type="date"
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={filters.end_date}
                            onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))}
                        />
                        <button
                            onClick={handleFilterClick}
                            disabled={filterLoading || !isAnyFilterFilled()}
                            className="bg-orange1 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
                        >
                            {filterLoading ? "Filtering..." : "Apply Filter"}
                        </button>
                        {isAnyFilterFilled() && (
                            <button
                                onClick={handleClearFilters}
                                disabled={clearFilterLoading}
                                className="bg-gray-500 text-white px-4 py-2 rounded text-sm"
                            >
                                {clearFilterLoading ? "Clearing..." : "Clear"}
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        expandableRows
                        expandableRowsComponent={ExpandableComponent}
                        pagination
                        customStyles={CustomStyles}
                        noDataComponent={<div className="p-4 text-center text-gray-500">No request history found</div>}
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default BranchInventoryRequestHistory;
